import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import {
  watchAuth,
  signOut as fbSignOut,
  getIdTokenResult,
  validateFirebaseConfig,
  createFirebaseConfigError,
} from './firebase';
import { http } from './http';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // firebase user object
  const [role, setRole] = useState(null);       // 'admin' | 'tech_support' | null
  const [ready, setReady] = useState(false);    // auth state resolved
  const [sessionExpired, setSessionExpired] = useState(false);
  const [authError, setAuthError] = useState(null);
  const firebaseConfig = useMemo(() => validateFirebaseConfig(), []);

  const refreshClaims = useCallback(async () => {
    try {
      const res = await getIdTokenResult(true);
      const r = res?.claims?.role || null;
      setRole(r);
      return r;
    } catch (e) {
      setRole(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let unsub = null;
    if (!firebaseConfig.ok) {
      setAuthError(createFirebaseConfigError(firebaseConfig.missing));
      setUser(null);
      setRole(null);
      setReady(true);
      return undefined;
    }
    try {
      unsub = watchAuth(async (fbUser) => {
        if (fbUser) {
          setUser(fbUser);
          let r = await refreshClaims();
          if (!r) {
            // Try login-hook — server may auto-grant via admin allowlist
            try {
              const resp = await http.post('/api/auth/login-hook');
              if (resp?.data?.upgraded) {
                // Force token refresh so custom claims are visible
                r = await refreshClaims();
              }
            } catch (e) {}
          }
          if (r) {
            // Fire server-side login audit when role is now present
            try { await http.post('/api/auth/login-hook'); } catch (e) {}
          }
        } else {
          setUser(null);
          setRole(null);
        }
        setAuthError(null);
        setReady(true);
      });
    } catch (err) {
      // Prevent blank-screen startup if auth SDK fails to initialize.
      console.error('Auth bootstrap failed', err);
      setAuthError(err);
      setUser(null);
      setRole(null);
      setReady(true);
    }
    return () => {
      if (typeof unsub === 'function') {
        unsub();
      }
    };
  }, [firebaseConfig, refreshClaims]);

  useEffect(() => {
    const onExpired = () => setSessionExpired(true);
    window.addEventListener('signal:session-expired', onExpired);
    return () => window.removeEventListener('signal:session-expired', onExpired);
  }, []);

  const logout = useCallback(async () => {
    try { await http.post('/api/auth/logout'); } catch (e) {}
    await fbSignOut();
    setUser(null);
    setRole(null);
    setSessionExpired(false);
  }, []);

  const value = {
    user,
    role,
    ready,
    authError,
    sessionExpired,
    isAuthenticated: !!user && !!role,
    isAdmin: role === 'admin',
    isTech: role === 'tech_support',
    refreshClaims,
    logout,
    clearSessionExpired: () => setSessionExpired(false),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
